'use client';

import { c as _c } from "react/compiler-runtime";
import { Button } from '@repo/ui/components/button';
import { cn } from '@repo/ui/lib/utils';
import { Flag, Plus, RotateCcw, X } from 'lucide-react';
import { ShopifyImage } from '@/components/ui/shopify-image';
import { formatMoneyAmount } from '@/lib/money';
import { type BundleEntry, useBundle } from './bundle-context';
function ordinal(n: number): string {
  const suffixes = ['th', 'st', 'nd', 'rd'];
  const value = n % 100;
  return `${n}${suffixes[(value - 20) % 10] ?? suffixes[value] ?? suffixes[0]}`;
}
function BundleLineItem(t0) {
  const $ = _c(29);
  const {
    entry,
    moneyLocale,
    onRemove
  } = t0;
  let t1;
  if ($[0] !== entry.selectedOptions) {
    t1 = entry.selectedOptions.filter(_temp).map(_temp2).join(" \xB7 ");
    $[0] = entry.selectedOptions;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const optionsLabel = t1;
  let t2;
  if ($[2] !== entry.image || $[3] !== entry.title) {
    t2 = entry.image && <ShopifyImage src={entry.image.url} alt={entry.image.altText || entry.title} fill={true} className="object-cover" sizes="56px" />;
    $[2] = entry.image;
    $[3] = entry.title;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t2) {
    t3 = <div className="relative h-16 w-14 shrink-0 overflow-hidden rounded bg-neutral-100">{t2}</div>;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== entry.title) {
    t4 = <h4 className="truncate text-[13px] font-bold uppercase text-neutral-900">{entry.title}</h4>;
    $[7] = entry.title;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== optionsLabel) {
    t5 = optionsLabel && <p className="mt-0.5 text-xs text-neutral-500">{optionsLabel}</p>;
    $[9] = optionsLabel;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== entry.currencyCode || $[12] !== entry.unitAmount || $[13] !== moneyLocale) {
    t6 = formatMoneyAmount(entry.unitAmount, entry.currencyCode, moneyLocale);
    $[11] = entry.currencyCode;
    $[12] = entry.unitAmount;
    $[13] = moneyLocale;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== t6) {
    t7 = <p className="mt-0.5 text-[13px] font-bold text-neutral-900">{t6}</p>;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== t4 || $[18] !== t5 || $[19] !== t7) {
    t8 = <div className="min-w-0 flex-1">{t4}{t5}{t7}</div>;
    $[17] = t4;
    $[18] = t5;
    $[19] = t7;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  const t9 = `Remove ${entry.title} from bundle`;
  let t10;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <X className="h-4 w-4" />;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== onRemove || $[23] !== t9) {
    t11 = <button type="button" onClick={onRemove} aria-label={t9} className="h-6 w-6 shrink-0 text-neutral-400 transition-colors hover:text-neutral-900">{t10}</button>;
    $[22] = onRemove;
    $[23] = t9;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== t11 || $[26] !== t3 || $[27] !== t8) {
    t12 = <div className="flex gap-3 rounded-lg border border-neutral-200 bg-white p-3">{t3}{t8}{t11}</div>;
    $[25] = t11;
    $[26] = t3;
    $[27] = t8;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  return t12;
}
function _temp2(option_0) {
  return option_0.value;
}
function _temp(option) {
  return option.value && option.value !== "Default Title";
}
function BundleHeader(t0) {
  const $ = _c(19);
  const {
    heading
  } = t0;
  const {
    preview
  } = useBundle();
  const {
    nextTier,
    currentTier,
    quantity,
    remainingToNext
  } = preview;
  const countLabel = nextTier ? `${quantity} of ${nextTier.minQuantity} added` : quantity > 0 ? "Max savings unlocked" : "Start your bundle";
  let t1;
  if ($[0] !== currentTier || $[1] !== nextTier || $[2] !== remainingToNext) {
    t1 = nextTier ? <>Add {remainingToNext} more {remainingToNext === 1 ? "tee" : "tees"} to unlock{" "}<span className="font-bold text-white">{nextTier.percentageDiscount}% off</span> your lowest item.</> : currentTier ? <>You've unlocked{" "}<span className="font-bold text-white">{currentTier.percentageDiscount}% off</span> — max savings!</> : null;
    $[0] = currentTier;
    $[1] = nextTier;
    $[2] = remainingToNext;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const unlockMessage = t1;
  let t2;
  if ($[4] !== heading) {
    t2 = <h2 className="text-base font-black uppercase tracking-wide">{heading}</h2>;
    $[4] = heading;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== countLabel) {
    t3 = <span className="text-xs font-bold uppercase tracking-wide text-neutral-400">{countLabel}</span>;
    $[6] = countLabel;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== t2 || $[9] !== t3) {
    t4 = <div className="flex items-baseline justify-between gap-2">{t2}{t3}</div>;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const t5 = `${preview.progressPercent}%`;
  let t6;
  if ($[11] !== t5) {
    t6 = <div className="relative mt-3 h-2 overflow-hidden rounded-full bg-white/15"><div className="absolute inset-y-0 left-0 rounded-full bg-brand-accent transition-[width] duration-500 ease-out" style={{
        width: t5
      }} /></div>;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== unlockMessage) {
    t7 = unlockMessage && <p className="mt-2.5 text-[13px] font-medium leading-snug text-neutral-300">{unlockMessage}</p>;
    $[13] = unlockMessage;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t4 || $[16] !== t6 || $[17] !== t7) {
    t8 = <div className="bg-neutral-950 px-4 py-4 text-white">{t4}{t6}{t7}</div>;
    $[15] = t4;
    $[16] = t6;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  return t8;
}
interface BundlePanelProps {
  onAddToCart: () => void;
  isSubmitting: boolean;
  className?: string;
}
export function BundlePanel(t0) {
  const $ = _c(63);
  const {
    onAddToCart,
    isSubmitting,
    className
  } = t0;
  const {
    entries,
    settings,
    preview,
    moneyLocale,
    removeVariant,
    clear
  } = useBundle();
  const {
    panel
  } = settings;
  const currency = preview.currencyCode;
  const isEmpty = entries.length === 0;
  const hasDiscount = preview.discountAmount > 0;
  const {
    nextTier,
    currentTier
  } = preview;
  let t1;
  if ($[0] !== className) {
    t1 = cn("flex flex-col overflow-hidden rounded-md border border-neutral-200 bg-white", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== panel.heading) {
    t2 = <BundleHeader heading={panel.heading} />;
    $[2] = panel.heading;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== clear || $[5] !== entries || $[6] !== isEmpty || $[7] !== moneyLocale || $[8] !== nextTier || $[9] !== panel.emptyText || $[10] !== preview.quantity || $[11] !== removeVariant) {
    t3 = <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-4">{isEmpty ? <p className="py-10 text-center text-sm text-neutral-500">{panel.emptyText}</p> : <><div className="flex items-center justify-between pt-4"><span className="text-[11px] font-bold uppercase tracking-wide text-neutral-500">Your items</span><button type="button" onClick={clear} className="text-[11px] font-semibold uppercase tracking-wide text-neutral-400 hover:text-neutral-900">Clear all</button></div><div className="mt-3 space-y-2.5">{entries.map(entry => <BundleLineItem key={entry.variantId} entry={entry} moneyLocale={moneyLocale} onRemove={() => removeVariant(entry.variantId)} />)}</div>{nextTier && <div className="mt-2.5 flex items-center justify-center gap-2 rounded-lg border border-dashed border-neutral-300 px-4 py-4 text-sm font-bold uppercase tracking-wide text-brand-accent"><Plus className="h-4 w-4" strokeWidth={3} />Add your {ordinal(preview.quantity + 1)} tee{preview.quantity + 1 === nextTier.minQuantity && ` — save ${nextTier.percentageDiscount}%`}</div>}</>}</div>;
    $[4] = clear;
    $[5] = entries;
    $[6] = isEmpty;
    $[7] = moneyLocale;
    $[8] = nextTier;
    $[9] = panel.emptyText;
    $[10] = preview.quantity;
    $[11] = removeVariant;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  let t4;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <span>Subtotal</span>;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== currency || $[15] !== moneyLocale || $[16] !== preview.subtotal) {
    t5 = formatMoneyAmount(preview.subtotal, currency, moneyLocale);
    $[14] = currency;
    $[15] = moneyLocale;
    $[16] = preview.subtotal;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  let t6;
  if ($[18] !== t5) {
    t6 = <div className="flex justify-between text-neutral-600">{t4}<span>{t5}</span></div>;
    $[18] = t5;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  let t7;
  if ($[20] !== currency || $[21] !== currentTier || $[22] !== hasDiscount || $[23] !== moneyLocale || $[24] !== preview.discountAmount) {
    t7 = hasDiscount && currentTier && <div className="flex justify-between font-medium text-brand-accent"><span>Bundle discount ({currentTier.percentageDiscount}%)</span><span>−{formatMoneyAmount(preview.discountAmount, currency, moneyLocale)}</span></div>;
    $[20] = currency;
    $[21] = currentTier;
    $[22] = hasDiscount;
    $[23] = moneyLocale;
    $[24] = preview.discountAmount;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  let t8;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <span>Shipping</span>;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  const t9 = panel.shippingNote || "Free";
  let t10;
  if ($[27] !== t9) {
    t10 = <div className="flex justify-between text-neutral-600">{t8}<span className="font-bold uppercase text-neutral-900">{t9}</span></div>;
    $[27] = t9;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  let t11;
  if ($[29] !== t10 || $[30] !== t6 || $[31] !== t7) {
    t11 = <div className="space-y-2 text-sm">{t6}{t7}{t10}</div>;
    $[29] = t10;
    $[30] = t6;
    $[31] = t7;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  let t12;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <span className="text-base font-black uppercase text-neutral-900">Total</span>;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  let t13;
  if ($[34] !== currency || $[35] !== hasDiscount || $[36] !== moneyLocale || $[37] !== preview.subtotal) {
    t13 = hasDiscount && <span className="text-sm font-medium text-neutral-400 line-through">{formatMoneyAmount(preview.subtotal, currency, moneyLocale)}</span>;
    $[34] = currency;
    $[35] = hasDiscount;
    $[36] = moneyLocale;
    $[37] = preview.subtotal;
    $[38] = t13;
  } else {
    t13 = $[38];
  }
  let t14;
  if ($[39] !== currency || $[40] !== moneyLocale || $[41] !== preview.total) {
    t14 = formatMoneyAmount(preview.total, currency, moneyLocale);
    $[39] = currency;
    $[40] = moneyLocale;
    $[41] = preview.total;
    $[42] = t14;
  } else {
    t14 = $[42];
  }
  let t15;
  if ($[43] !== t14) {
    t15 = <span className="text-2xl font-black text-neutral-900">{t14}</span>;
    $[43] = t14;
    $[44] = t15;
  } else {
    t15 = $[44];
  }
  let t16;
  if ($[45] !== t13 || $[46] !== t15) {
    t16 = <div className="mt-3 flex items-baseline justify-between border-t border-neutral-200 pt-3">{t12}<span className="flex items-baseline gap-2">{t13}{t15}</span></div>;
    $[45] = t13;
    $[46] = t15;
    $[47] = t16;
  } else {
    t16 = $[47];
  }
  const t17 = isEmpty || isSubmitting;
  const t18 = isSubmitting ? "Adding\u2026" : panel.addCtaLabel;
  let t19;
  if ($[48] !== onAddToCart || $[49] !== t17 || $[50] !== t18) {
    t19 = <Button type="button" variant="accent" className="mt-4 w-full rounded-md py-6 text-sm font-bold uppercase tracking-wider" onClick={onAddToCart} disabled={t17}>{t18}</Button>;
    $[48] = onAddToCart;
    $[49] = t17;
    $[50] = t18;
    $[51] = t19;
  } else {
    t19 = $[51];
  }
  let t20;
  if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <span className="flex items-center gap-1.5"><Flag className="h-3.5 w-3.5" />Printed in USA</span>;
    $[52] = t20;
  } else {
    t20 = $[52];
  }
  let t21;
  if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <div className="mt-3 flex items-center justify-center gap-5 text-[11px] font-semibold uppercase tracking-wide text-neutral-500">{t20}<span className="flex items-center gap-1.5"><RotateCcw className="h-3.5 w-3.5" />30-Day Returns</span></div>;
    $[53] = t21;
  } else {
    t21 = $[53];
  }
  let t22;
  if ($[54] !== t11 || $[55] !== t16 || $[56] !== t19) {
    t22 = <div className="border-t border-neutral-200 p-4">{t11}{t16}{t19}{t21}</div>;
    $[54] = t11;
    $[55] = t16;
    $[56] = t19;
    $[57] = t22;
  } else {
    t22 = $[57];
  }
  let t23;
  if ($[58] !== t1 || $[59] !== t2 || $[60] !== t22 || $[61] !== t3) {
    t23 = <div className={t1}>{t2}{t3}{t22}</div>;
    $[58] = t1;
    $[59] = t2;
    $[60] = t22;
    $[61] = t3;
    $[62] = t23;
  } else {
    t23 = $[62];
  }
  return t23;
}